import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

const blogPosts = [
  {
    slug: 'ehs-acronym-soup',
    title: "🥣 Welcome to EHS Acronym Soup",
    excerpt: "If you've ever heard 'update the SPCC per EPA under CWA' and nodded like you understood it... congratulations, you speak EHS. Welcome to Acronym Soup - where EPA, OSHA, LOTO, and 150+ other acronyms make perfect sense.",
    date: "February 19, 2025",
    readTime: "8 min read",
    category: "EHS Basics",
    image: "/blog-images/ehs-acronym-soup.jpg",
    featured: true  // This makes it the featured post
  },
  {
    slug: 'do-i-need-an-ehs-manager',
    title: "Do I Need a Full-Time EHS Manager? (Honest Answer for Small Businesses)",
    excerpt: "It is one of the most common questions small business owners ask. The honest answer: for most companies under 50 employees, no — not yet. Here is how to think through it and what actually works instead.",
    date: "May 22, 2025",
    readTime: "7 min read",
    category: "EHS Management",
    image: "/blog-images/ehs-manager.jpg",
    featured: false
  },
  {
    slug: 'workers-comp-audit-what-insurers-look-for',
    title: "Workers Comp Audit: What Your Insurer Is Actually Looking For",
    excerpt: "Every year your workers comp insurer audits your business. Most owners just try to get through it. That mindset is costing them money. Here is what insurers actually evaluate — and how to use it to lower your premiums.",
    date: "May 15, 2025",
    readTime: "8 min read",
    category: "Insurance Readiness",
    image: "/blog-images/workers-comp-audit.jpg",
    featured: false
  },
  {
    slug: 'ehs-compliance-checklist-small-business',
    title: "EHS Compliance Checklist for Small Businesses: Where to Start",
    excerpt: "If someone just handed you EHS responsibility and you are not sure where to begin, start here. A practical checklist covering OSHA fundamentals, hazard communication, PPE, recordkeeping, and environmental basics — without the overwhelm.",
    date: "May 8, 2025",
    readTime: "9 min read",
    category: "EHS Basics",
    image: "/blog-images/ehs-compliance-checklist.jpg",
    featured: false
  },
  {
    slug: 'osha-violation-costs',
    title: "How Much Does an OSHA Violation Actually Cost? (The Numbers Will Surprise You)",
    excerpt: "An OSHA fine can reach $16,550 per violation — but the fine is often the cheapest part. Add legal fees, increased insurance premiums, lost productivity, and potential litigation and a single citation can cost a small business $50,000 to $100,000 or more.",
    date: "May 1, 2025",
    readTime: "7 min read",
    category: "OSHA Compliance",
    image: "/blog-images/osha-violation-costs.jpg",
    featured: false
  },
  {
    slug: 'process-safety-psm',
    title: "Process Safety & PSM: The Rules You Didn't Know You Were Playing By",
    excerpt: "When most people hear 'safety,' they think hard hats and safety glasses. But process safety? That's a different animal. If your company uses certain chemicals or operates specific equipment, you may already be subject to PSM without even realizing it.",
    date: "February 12, 2025",
    readTime: "7 min read",
    category: "Process Safety",
    image: "/blog-images/process-safety-psm.jpg",
    featured: false
  },
  {
    slug: 'what-is-ehs',
    title: "What the Heck Is EHS, Anyway?",
    excerpt: "If you've ever heard someone say 'EHS will want to see that' and nodded politely while secretly thinking 'cool, but what does that mean?' — you're not alone. Learn what EHS really means in plain English.",
    date: "February 4, 2025",
    readTime: "6 min read",
    category: "EHS Basics",
    image: "/blog-images/what-is-ehs.jpg",
    featured: false
  }
];

const Blog = () => {
  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = blogPosts; // Show all posts in Recent Articles section

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="First Step EHS Blog - Workplace Safety & OSHA Compliance Insights"
        description="Expert insights on EHS, process safety, OSHA compliance, and workplace safety. Practical guidance for environmental health and safety professionals."
        keywords="EHS blog, workplace safety, OSHA compliance, process safety, PSM, environmental health safety, safety management"
        canonical="https://firststepehs.com/blog"
      />
      
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-semibold">
            ← Back to Home
          </Link>
        </div>
      </header>

      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            First Step EHS Blog
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl">
            Practical insights on environmental health, workplace safety, and OSHA compliance — explained in plain English.
          </p>
        </div>
      </div>

      {featuredPost && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <Link to={`/blog/${featuredPost.slug}`} className="block">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="md:flex">
                <div className="md:w-1/2 h-64 md:h-auto bg-gradient-to-r from-blue-500 to-blue-700 relative overflow-hidden">
                  {featuredPost.image && (
                    <img 
                      src={featuredPost.image} 
                      alt={featuredPost.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="md:w-1/2 p-8">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="text-blue-600 font-semibold">{featuredPost.category}</span>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{featuredPost.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{featuredPost.readTime}</span>
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-4 hover:text-blue-600 transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center text-blue-600 font-semibold hover:text-blue-700">
                    Read Article
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      )}

      {regularPosts.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">Recent Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post) => (
              <Link 
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="block bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="h-48 bg-gradient-to-r from-blue-500 to-blue-700 relative overflow-hidden">
                  {post.image && (
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span className="text-blue-600 font-semibold">{post.category}</span>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-blue-600 font-semibold text-sm">
                    Read More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated on EHS Best Practices</h2>
          <p className="text-xl text-blue-100 mb-8">
            Get practical EHS tips, compliance updates, and industry insights delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-colors">
              Subscribe
            </button>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            No spam. Unsubscribe anytime.
          </p>
        </div>
      </div>

      <div className="bg-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Ready to Assess Your EHS Program?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Take our free assessment and get an instant report on your workplace safety and compliance status.
          </p>
          <Link
            to="/"
            className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors shadow-lg"
          >
            Start Free Assessment
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Blog;